<template>
  <div class="content">
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <div class="loading-text">Loading...</div>
    </div>
    <!-- 社員情報 -->
    <div class="employee-info">
      <h2>Employee ID: {{ selectedEmployee.id }} - Name: {{ selectedEmployee.name }}</h2>
    </div>

    <!-- 部下の選択 -->
    <div class="subordinates-list">
      <h3>Attendance Check Progress</h3>
      <ul>
        <li v-for="subordinate in subordinates" :key="subordinate.Id" class="subordinate-item">
          <input type="radio" :value="subordinate.Id" v-model="selectedSubordinate" @change="fetchAttendanceData">
          <span>{{ subordinate.Username }}</span>
          <div class="progress-container">
            <div class="progress-bar-container">
              <div class="progress-bar" :style="progressBarStyle(subordinate.Id)"></div>
            </div>
            <div :style="progressTextStyle(subordinate.Id)">{{ progressPercentage(subordinate.Id) }}%</div>
          </div>
        </li>
      </ul>
    </div>

    <!-- PTO情報 -->
    <div class="pto-section">
      <div class="pto-table">
        <table>
          <thead>
            <tr>
              <th colspan="2">{{ fiscalYear }} PTO</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Total</td>
              <td>{{ formattedTotalPTO }}</td>
            </tr>
            <tr>
              <td>Used</td>
              <td>{{ formattedUsedPTO }}</td>
            </tr>
            <tr>
              <td :class="{'remain-pto-negative': remainingPTOHours <= 0}">Remain</td>
              <td :class="{'remain-pto-negative': remainingPTOHours <= 0}">{{ formattedRemainingPTO }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="pto-table">
        <table>
          <thead>
            <tr>
              <th colspan="2">{{ fiscalYear }} SickLeave</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Total</td>
              <td>{{ formattedTotalSickLeave }}</td>
            </tr>
            <tr>
              <td>Used</td>
              <td>{{ formattedUsedSickLeave }}</td>
            </tr>
            <tr>
              <td :class="{'remain-pto-negative': remainingSickLeaveHours <= 0}">Remain</td>
              <td :class="{'remain-pto-negative': remainingSickLeaveHours <= 0}">{{ formattedRemainingSickLeave }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Date and Month Switcher -->
      <div class="date-switcher">
        <button @click="moveBackward">
          <b>&lt; Previous &boxv;&boxv;</b>
        </button>
        <span>{{ displayDate }}</span>
        <button @click="moveForward">
          <b>&boxv;&boxv; Next &gt;</b>
        </button>
      </div>
    </div>

    <!-- 日にちを含む表 -->
    <div class="date-table full-width">
      <table>
        <thead>
          <tr>
            <th>Date</th>
            <th>Start</th>
            <th>Mid End</th>
            <th>Mid Start</th>
            <th>End</th>
            <th>Break</th>
            <th>PTO</th>
            <th>Sick Lv</th>
            <th>Hours</th>
            <th>OT</th>
            <th>OT Order</th> <!-- OT Orderを追加 -->
            <th>Abs</th>
            <th>Self</th>
            <th>Sup</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="date in dates" :key="date.day" :class="{ 'confirmed': date.supervisorConfirmed }">
            <td>{{ date.day }}</td>
            <td>
              <input type="time" v-model="date.start" disabled>
            </td>
            <td>
              <input type="time" v-model="date.partialEnd" disabled>
            </td>
            <td>
              <input type="time" v-model="date.partialStart" disabled>
            </td>
            <td>
              <input type="time" v-model="date.end" disabled>
            </td>
            <td>{{ date.break }}</td>
            <td>{{ date.paidLeave }}</td>
            <td>{{ date.other }}</td>
            <td>{{ date.hours }}</td>
            <td>{{ date.overtime }}</td>
            <td :class="{ 'highlighted': date.otOrder !== '0:00' }">{{ date.otOrder }}</td> <!-- OT Orderの表示 -->
            <td>{{ date.absence }}</td>
            <td>
              <button @click="toggleConfirmation(date)" disabled>{{ date.confirmed ? 'Undo' : 'Confirm' }}</button>
            </td>
            <td>
              <button @click="toggleSupervisorConfirmation(date)"
                      :style="date.supervisorConfirmed ? { backgroundColor: 'green', color: 'white' } : { backgroundColor: 'red', color: 'white' }">
                {{ date.supervisorConfirmed ? 'Undo' : 'Confirm' }}
              </button>
            </td>
          </tr>
          <tr>
            <td><strong>Total</strong></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td><strong>{{ totalPaidLeave }}</strong></td>
            <td><strong>{{ totalOther }}</strong></td>
            <td><strong>{{ totalWorkHours }}</strong></td>
            <td><strong>{{ totalOvertime }}</strong></td>
            <td><strong>{{ totalOTOrder }}</strong></td> <!-- OT Orderの合計 -->
            <td><strong>{{ totalAbsence }}</strong></td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL

export default {
  data () {
    return {
      currentYear: new Date().getFullYear(),
      currentMonth: new Date().getMonth() + 1,
      currentDate: new Date().getDate(),
      selectedEmployee: { id: '', name: '' },
      subordinates: [],
      selectedSubordinate: null,
      dates: [],
      attendanceData: {},
      totalPTOHours: 0,
      usedPTOHours: 0,
      remainingPTOHours: 0,
      totalSickLeaveHours: 0,
      usedSickLeaveHours: 0,
      remainingSickLeaveHours: 0,
      holidays: [],
      loadingCount: 0
    }
  },
  computed: {
    isLoading () {
      return this.loadingCount > 0
    },
    displayDate () {
      const monthHalf = this.currentDate < 15 ? 'FIRST Half' : 'SECOND Half'
      return `Year: ${this.currentYear} / Month: ${this.currentMonth} / ${monthHalf}`
    },
    fiscalYear () {
      const current = new Date(Date.UTC(this.currentYear, this.currentMonth - 1, this.currentDate))
      const fiscalYearStart = new Date(Date.UTC(current.getUTCFullYear(), 3, 1))
      return current >= fiscalYearStart ? `FY${current.getUTCFullYear()}` : `FY${current.getUTCFullYear() - 1}`
    },
    totalWorkHours () {
      return this.sumTimes(this.dates.map(date => date.hours))
    },
    totalOvertime () {
      return this.sumTimes(this.dates.map(date => date.overtime))
    },
    totalAbsence () {
      return this.sumTimes(this.dates.map(date => date.absence))
    },
    totalPaidLeave () {
      return this.sumTimes(this.dates.map(date => date.paidLeave))
    },
    totalOther () {
      return this.sumTimes(this.dates.map(date => date.other))
    },
    totalOTOrder () {
      return this.sumTimes(this.dates.map(date => date.otOrder))
    },
    formattedUsedPTO () {
      return this.hoursToDaysAndTimeString(this.usedPTOHours)
    },
    formattedRemainingPTO () {
      return this.hoursToDaysAndTimeString(this.remainingPTOHours)
    },
    formattedTotalPTO () {
      return this.hoursToDaysAndTimeString(this.totalPTOHours)
    },
    formattedTotalSickLeave () {
      return this.hoursToDaysAndTimeString(this.totalSickLeaveHours)
    },
    formattedUsedSickLeave () {
      return this.hoursToDaysAndTimeString(this.usedSickLeaveHours)
    },
    formattedRemainingSickLeave () {
      return this.hoursToDaysAndTimeString(this.remainingSickLeaveHours)
    }
  },
  methods: {
    startLoading () {
      this.loadingCount++
    },

    finishLoading () {
      this.loadingCount = Math.max(this.loadingCount - 1, 0)
    },

    withLoading (request) {
      this.startLoading()

      return request()
        .finally(() => {
          this.finishLoading()
        })
    },
    checkAuth () {
      const authToken = localStorage.getItem('authToken')
      const employeeId = localStorage.getItem('employeeId')
      if (!authToken || !employeeId) {
        this.$router.push({ name: 'login' })
      } else {
        this.fetchEmployeeData(employeeId)
      }
    },
    fetchEmployeeData (employeeId) {
      this.withLoading(() => axios.get(`${API_BASE_URL}/employee/${employeeId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      }))
        .then(response => {
          this.selectedEmployee.id = response.data.ID
          this.selectedEmployee.name = response.data.Username
          this.fetchSubordinates()
        })
        .catch(error => {
          if (error.response && error.response.status === 401) {
            localStorage.removeItem('authToken')
            localStorage.removeItem('employeeId')
            this.$router.push({ name: 'login' })
          }
        })
    },
    fetchSubordinates () {
      this.withLoading(() => axios.get(`${API_BASE_URL}/subordinates/${this.selectedEmployee.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      }))
        .then(response => {
          this.subordinates = response.data.sort((a, b) => a.Username.localeCompare(b.Username))

          if (this.subordinates.length > 0) {
            this.selectedSubordinate = this.subordinates[0].Id
            this.fetchAttendanceData(this.selectedSubordinate)
            this.fetchEmployeePTO(this.selectedSubordinate)
            this.fetchEmployeeSickLeave(this.selectedSubordinate)
            this.fetchOTOrderData(this.selectedSubordinate)
          }

          this.fetchAllAttendanceData()
        })
        .catch(error => {
          console.error('Error fetching subordinate data:', error)
        })
    },
    fetchAllAttendanceData () {
      const requests = this.subordinates.map(subordinate => {
        return this.fetchAttendanceData(subordinate.Id)
      })
      Promise.all(requests)
        .then(() => {
          console.log('All attendance data fetched')
        })
        .catch(error => {
          console.error('Error fetching all attendance data:', error)
        })
    },
    fetchAttendanceData (subordinateId) {
      const month = `${this.currentYear}-${String(this.currentMonth).padStart(2, '0')}`
      const startDate = this.currentDate < 15 ? `${month}-01` : `${month}-16`
      const endDate = this.currentDate < 15 ? `${month}-15` : `${month}-${new Date(this.currentYear, this.currentMonth, 0).getDate()}`

      return this.withLoading(() => axios.get(`${API_BASE_URL}/supervisor/attendance/${subordinateId}/${startDate}/${endDate}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      }))
        .then(response => {
          const attendanceData = response.data
          this.attendanceData = { ...this.attendanceData, [subordinateId]: attendanceData }
          if (subordinateId === this.selectedSubordinate) {
            this.updateDatesWithAttendance(attendanceData)
            this.fetchEmployeePTO(subordinateId)
            this.fetchEmployeeSickLeave(subordinateId)
            this.fetchOTOrderData(subordinateId)
          }
        })
        .catch(error => {
          console.error('Error fetching attendance data:', error)
        })
    },
    fetchEmployeePTO (subordinateId) {
      this.withLoading(() => axios.get(`${API_BASE_URL}/pto/${subordinateId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      }))
        .then(response => {
          const fiscalYearNumber = parseInt(this.fiscalYear.replace('FY', ''), 10)
          const ptoData = response.data.find(data => data.Year === fiscalYearNumber)
          if (ptoData) {
            this.totalPTOHours = ptoData.PTO * 7.5
            this.fetchAnnualPTOData(subordinateId)
          } else {
            this.totalPTOHours = 0
            this.usedPTOHours = 0
            this.remainingPTOHours = 0
          }
        })
        .catch(error => {
          console.error('Error fetching PTO data:', error)
        })
    },
    fetchAnnualPTOData (subordinateId) {
      const fiscalYearNumber = parseInt(this.fiscalYear.replace('FY', ''), 10)
      const startOfFiscalYear = `${fiscalYearNumber}-04-01`
      const endOfFiscalYear = `${fiscalYearNumber + 1}-03-31`

      this.withLoading(() => axios.get(`${API_BASE_URL}/ptoapprove/${subordinateId}/year/${fiscalYearNumber}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        },
        params: {
          startDate: startOfFiscalYear,
          endDate: endOfFiscalYear
        }
      }))
        .then(response => {
          const ptoApprovalData = response.data
          this.usedPTOHours = ptoApprovalData.reduce((acc, entry) => acc + this.timeToHours(entry.PTO), 0)
          this.calculateRemainingPTO()
        })
        .catch(error => {
          console.error('Error fetching PTO approval data:', error)
        })
    },
    calculateRemainingPTO () {
      this.remainingPTOHours = this.totalPTOHours - this.usedPTOHours
    },
    fetchEmployeeSickLeave (subordinateId) {
      this.withLoading(() => axios.get(`${API_BASE_URL}/sickleave/${subordinateId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      }))
        .then(response => {
          const sickLeaveData = response.data
          if (sickLeaveData.length > 0) {
            this.totalSickLeaveHours = sickLeaveData[0].SickLeave * 7.5
            this.fetchAnnualSickLeaveApprovalData(subordinateId)
          } else {
            this.totalSickLeaveHours = 0
            this.usedSickLeaveHours = 0
            this.remainingSickLeaveHours = 0
          }
        })
        .catch(error => {
          console.error('Error fetching Sick Leave data:', error)
        })
    },
    fetchAnnualSickLeaveApprovalData (subordinateId) {
      const fiscalYearNumber = parseInt(this.fiscalYear.replace('FY', ''), 10)
      this.withLoading(() => axios.get(`${API_BASE_URL}/sickleaveapprove/${subordinateId}/year/${fiscalYearNumber}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      }))
        .then(response => {
          const sickLeaveApprovalData = response.data
          this.usedSickLeaveHours = sickLeaveApprovalData.reduce((acc, date) => acc + this.timeToHours(date.SickLeave), 0)
          this.remainingSickLeaveHours = this.totalSickLeaveHours - this.usedSickLeaveHours
        })
        .catch(error => {
          console.error('Error fetching annual Sick Leave approval data:', error)
        })
    },
    fetchOTOrderData (subordinateId) {
      const fiscalYearNumber = parseInt(this.fiscalYear.replace('FY', ''), 10)
      const startOfFiscalYear = `${fiscalYearNumber}-04-01`
      const endOfFiscalYear = `${fiscalYearNumber + 1}-03-31`

      this.withLoading(() => axios.get(`${API_BASE_URL}/overtime/${subordinateId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        },
        params: {
          startDate: startOfFiscalYear,
          endDate: endOfFiscalYear
        }
      }))
        .then(response => {
          const otOrderData = response.data
          this.dates = this.dates.map(date => {
            const otOrder = otOrderData.find(data => data.Date.split('T')[0] === date.day)
            return {
              ...date,
              otOrder: otOrder ? otOrder.OverTimeOrder.slice(0, 5) : '0:00'
            }
          })
        })
        .catch(error => {
          console.error('Error fetching OT Order data:', error)
        })
    },
    updateDatesWithAttendance (attendanceData) {
      this.dates = attendanceData.map(date => {
        return {
          day: date.Date.split('T')[0],
          start: date.InTime ? date.InTime.slice(0, 5) : '',
          partialEnd: date.MidOutTime === '00:00:00' ? '' : date.MidOutTime ? date.MidOutTime.slice(0, 5) : '',
          partialStart: date.MidInTime === '00:00:00' ? '' : date.MidInTime ? date.MidInTime.slice(0, 5) : '',
          end: date.OutTime ? date.OutTime.slice(0, 5) : '',
          break: date.BreakTime ? date.BreakTime.slice(0, 5) : '0:00',
          paidLeave: date.PTO ? date.PTO.slice(0, 5) : '0:00',
          other: date.Others ? date.Others.slice(0, 5) : '0:00',
          otOrder: date.otOrder || '0:00', // OT Orderの初期化
          hours: date.WorkHours ? date.WorkHours.slice(0, 5) : '',
          overtime: date.OvertimeHours ? date.OvertimeHours.slice(0, 5) : '',
          absence: date.AbsentHours ? date.AbsentHours.slice(0, 5) : '',
          confirmed: date.SelfConf === 1,
          supervisorConfirmed: date.SupervisorConf === 1
        }
      })
        .sort((a, b) => new Date(a.day) - new Date(b.day))

      this.calculateAllTimes()
    },
    calculateAllTimes () {
      this.dates.forEach(date => {
        date.break = this.calculateBreak(date.start, date.end, date.partialEnd, date.partialStart)
        date.hours = this.calculateWorkHours(date.start, date.partialEnd, date.partialStart, date.end, date.break, date.paidLeave, date.other)
        date.overtime = this.calculateOvertime(date.start, date.partialEnd, date.partialStart, date.end, date.break)
        date.absence = this.calculateAbsence(date.hours, date.paidLeave, date.other)
      })
      this.updateTotalTimes()
    },
    calculateBreak (start, end, partialEnd = null, partialStart = null) {
      const startTime = new Date(`1970-01-01T${start}Z`)
      const endTime = new Date(`1970-01-01T${end}Z`)
      const partialEndTime = partialEnd ? new Date(`1970-01-01T${partialEnd}Z`) : null
      const partialStartTime = partialStart ? new Date(`1970-01-01T${partialStart}Z`) : null

      if (!partialEnd && !partialStart) {
        const duration = (endTime - startTime) / (1000 * 60 * 60)
        return duration >= 6 ? '1:00' : '0:00'
      } else if (partialEnd === '00:00' && partialStart === '00:00') {
        const duration = (endTime - startTime) / (1000 * 60 * 60)
        return duration >= 6 ? '1:00' : '0:00'
      } else {
        if ((partialStartTime - partialEndTime) / (1000 * 60 * 60) >= 6 || (endTime - partialStartTime) / (1000 * 60 * 60) >= 6) {
          return '1:00'
        }
      }
      return '0:00'
    },
    calculateWorkHours (start, partialEnd, partialStart, end, breakTime, paidLeave, other) {
      if (start === '00:00' && end === '00:00') {
        return '00:00'
      }

      const startTime = new Date(`1970-01-01T${start}Z`)
      const partialEndTime = partialEnd ? new Date(`1970-01-01T${partialEnd}Z`) : null
      const partialStartTime = partialStart ? new Date(`1970-01-01T${partialStart}Z`) : null
      const endTime = new Date(`1970-01-01T${end}Z`)

      let duration = 0
      if (partialEndTime && partialStartTime) {
        duration = (partialEndTime - startTime + (endTime - partialStartTime)) / (1000 * 60 * 60)
      } else {
        duration = (endTime - startTime) / (1000 * 60 * 60)
      }

      const breakHours = this.timeToHours(breakTime)
      const workHours = duration - breakHours
      return this.hoursToTimeString(workHours)
    },
    calculateOvertime (start, partialEnd, partialStart, end, breakTime) {
      const startTime = new Date(`1970-01-01T${start}Z`)
      const partialEndTime = partialEnd ? new Date(`1970-01-01T${partialEnd}Z`) : null
      const partialStartTime = partialStart ? new Date(`1970-01-01T${partialStart}Z`) : null
      const endTime = new Date(`1970-01-01T${end}Z`)

      let workDuration = 0
      if (partialEndTime && partialStartTime) {
        workDuration = (partialEndTime - startTime + (endTime - partialStartTime)) / (1000 * 60 * 60)
      } else {
        workDuration = (endTime - startTime) / (1000 * 60 * 60)
      }

      const breakHours = this.timeToHours(breakTime)
      const overtime = Math.max(workDuration - breakHours - 7.5, 0)
      return this.hoursToTimeString(overtime)
    },
    calculateAbsence (workHours, paidLeave, other) {
      const requiredHours = 7.5 - this.timeToHours(paidLeave) - this.timeToHours(other)
      const workHoursFloat = this.timeToHours(workHours)
      const absence = requiredHours - workHoursFloat
      return this.hoursToTimeString(Math.max(absence, 0))
    },
    sumTimes (times) {
      const totalMinutes = times.reduce((acc, time) => {
        if (!time) return acc
        const [hours, minutes] = time.split(':').map(Number)
        return acc + hours * 60 + minutes
      }, 0)

      const h = Math.floor(totalMinutes / 60)
      const m = totalMinutes % 60
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
    },
    timeToHours (timeStr) {
      if (typeof timeStr !== 'string') return 0
      const parts = timeStr.split(':')
      return parseFloat(parts[0]) + parseFloat(parts[1]) / 60
    },
    hoursToDaysAndTimeString (hours) {
      const days = (hours / 7.5).toFixed(2)
      const h = Math.floor(hours)
      const m = Math.round((hours - h) * 60)
      return `${days} Days (${h.toString().padStart(2, '0')}:${(m % 60).toString().padStart(2, '0')})`
    },
    hoursToTimeString (hours) {
      const h = Math.floor(hours)
      const m = Math.round((hours - h) * 60)
      return `${h.toString().padStart(2, '0')}:${(m % 60).toString().padStart(2, '0')}`
    },
    moveForward () {
      this.adjustDate(true)
      this.fetchHolidaysForCurrentWindow()
      this.fetchAllAttendanceData()
    },
    moveBackward () {
      this.adjustDate(false)
      this.fetchHolidaysForCurrentWindow()
      this.fetchAllAttendanceData()
    },
    adjustDate (forward) {
      const currentDate = new Date(this.currentYear, this.currentMonth - 1, this.currentDate)
      if (forward) {
        if (this.currentDate < 15) {
          this.currentDate = 16
        } else {
          currentDate.setMonth(currentDate.getMonth() + 1)
          this.currentYear = currentDate.getFullYear()
          this.currentMonth = currentDate.getMonth() + 1
          this.currentDate = 1
        }
      } else {
        if (this.currentDate > 15) {
          this.currentDate = 1
        } else {
          currentDate.setMonth(currentDate.getMonth() - 1)
          this.currentYear = currentDate.getFullYear()
          this.currentMonth = currentDate.getMonth() + 1
          this.currentDate = 16
        }
      }
    },
    fetchHolidays (year) {
      this.withLoading(() => axios.get(`${API_BASE_URL}/holidays/${year}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      }))
        .then(response => {
          this.holidays = response.data
        })
        .catch(error => {
          console.error('Error fetching holidays:', error)
        })
    },
    fetchHolidaysForCurrentWindow () {
      const years = [
        this.currentYear - 1,
        this.currentYear,
        this.currentYear + 1
      ]

      return this.withLoading(() => Promise.all(
        years.map(year => axios.get(`${API_BASE_URL}/holidays/${year}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`
          }
        }))
      ))
        .then(responses => {
          const merged = responses.flatMap(response => response.data)
          const uniqueMap = new Map()

          merged.forEach(holiday => {
            uniqueMap.set(`${holiday.holiday_name}-${holiday.holiday_date}`, holiday)
          })

          this.holidays = Array.from(uniqueMap.values())
        })
        .catch(error => {
          console.error('Error fetching holidays:', error)
        })
    },
    progressBarStyle (subordinateId) {
      const percentage = this.progressPercentage(subordinateId)
      return {
        width: `${percentage}%`,
        backgroundColor: percentage === 100 ? 'green' : 'blue'
      }
    },
    progressTextStyle (subordinateId) {
      const percentage = this.progressPercentage(subordinateId)
      return {
        color: percentage === 100 ? 'green' : 'black',
        fontWeight: percentage === 100 ? 'bold' : 'normal'
      }
    },
    progressPercentage (subordinateId) {
      const attendance = this.attendanceData[subordinateId] || []
      const totalDays = this.calculateTotalDays()
      const confirmed = attendance.filter(date => date.SupervisorConf === 1 && !this.isWeekend(date.Date.split('T')[0]) && !this.isHoliday(date.Date.split('T')[0])).length
      return totalDays === 0 ? 0 : Math.round((confirmed / totalDays) * 100)
    },
    calculateTotalDays () {
      const startDate = new Date(this.currentYear, this.currentMonth - 1, this.currentDate < 15 ? 1 : 16)
      const endDate = new Date(this.currentYear, this.currentMonth - 1, this.currentDate < 15 ? 15 : new Date(this.currentYear, this.currentMonth, 0).getDate())
      let totalDays = 0
      let currentDate = new Date(startDate)

      while (currentDate <= endDate) {
        if (!this.isWeekend(currentDate.toISOString().split('T')[0]) && !this.isHoliday(currentDate.toISOString().split('T')[0])) {
          totalDays++
        }
        currentDate = new Date(currentDate.setDate(currentDate.getDate() + 1))
      }
      return totalDays
    },
    isWeekend (dateStr) {
      const date = new Date(dateStr + 'T00:00:00Z')
      const day = date.getUTCDay()
      return day === 0 || day === 6
    },
    isHoliday (dateStr) {
      return this.holidays.some(holiday => holiday.holiday_date === dateStr)
    },
    updateTotalTimes () {
      this.$forceUpdate()
    },
    toggleSupervisorConfirmation (date) {
      date.supervisorConfirmed = !date.supervisorConfirmed
      this.updateSupervisorConfirmation(date)
    },
    updateSupervisorConfirmation (date) {
      const confirmationData = {
        EmployeeID: this.selectedSubordinate,
        Date: date.day,
        SupervisorConf: date.supervisorConfirmed ? 1 : 0
      }
      this.withLoading(() => axios.put(`${API_BASE_URL}/attendance/supervisor-confirm`, confirmationData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      }))
        .then(response => {
          console.log('Supervisor confirmation updated:', response.data)
          this.fetchAttendanceData(this.selectedSubordinate)
        })
        .catch(error => {
          console.error('Error updating supervisor confirmation:', error)
        })
    }
  },
  watch: {
    selectedSubordinate (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.fetchAttendanceData(newVal)
        this.fetchEmployeePTO(newVal)
        this.fetchEmployeeSickLeave(newVal)
        this.fetchOTOrderData(newVal)
      }
    },
    fiscalYear (newVal, oldVal) {
      if (newVal !== oldVal) {
        if (this.selectedSubordinate) {
          this.fetchEmployeePTO(this.selectedSubordinate)
          this.fetchEmployeeSickLeave(this.selectedSubordinate)
          this.fetchOTOrderData(this.selectedSubordinate)
        }
      }
    }
  },
  created () {
    this.checkAuth()
    this.fetchHolidaysForCurrentWindow()
  }
}
</script>

<style scoped>
.content {
  display: flex;
  flex-direction: column;
  align-items: start;
  margin: 20px;
}

.date-switcher, .employee-info, .date-table {
  margin-bottom: 20px;
}

.date-switcher {
  display: flex;
  align-items: center;
}

.date-switcher button {
  background: none;
  border: none;
}

.date-table table {
  width: 100%;
  border-collapse: collapse;
}

th,
td {
  text-align: center;
  padding: 8px;
  border-bottom: 1px solid #ddd;
}

th {
  background-color: #f2f2f2;
}

.confirmed {
  background-color: #e0e0e0; /* Light gray background for confirmed rows */
}

.full-width {
  width: 100%;
}

.subordinates-list ul {
  list-style-type: none;
  padding: 0;
}

.subordinate-item {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}

.progress-container {
  display: flex;
  align-items: center;
  margin-left: 10px;
}

.progress-bar-container {
  width: 100px;
  height: 10px;
  background-color: #ddd;
  margin-right: 5px;
}

.progress-bar {
  height: 100%;
}

.progress-text {
  margin-left: 5px;
}

.pto-section {
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  width: 100%;
  margin-bottom: 20px;
}

.pto-table {
  flex: 0 0 auto;
  margin-right: 20px;
}

.pto-table table {
  border-collapse: collapse;
}

.pto-table th, .pto-table td {
  text-align: left;
  padding: 8px;
  border-bottom: 1px solid #ddd;
  white-space: nowrap;
}

.pto-table th {
  background-color: #f2f2f2;
}

.date-switcher {
  display: flex;
  align-items: center;
}

.date-switcher span {
  font-size: 1em;
  font-weight: bold;
  margin: 0 10px;
}

.date-switcher button {
  font-size: 1em;
  font-weight: bold;
}

.remain-pto-negative {
  color: red;
  font-weight: bold;
}

.highlighted {
  color: red; /* OT Orderが0:00以外の場合に赤字で表示 */
}
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.65);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 5px solid #ccc;
  border-top: 5px solid #333;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loading-text {
  margin-top: 12px;
  font-weight: bold;
  font-size: 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
